import { inject } from '@angular/core';
import { GuardResult, MaybeAsync, Routes } from '@angular/router';
import { DirectoryClient } from 'api-clients/agency-admin-api-client';
import { User } from 'api-clients/identity-server-api-client';
import { IdpCarrierManagementClient } from 'api-clients/rating-api-client';
import { CarriersClient } from 'api-clients/submission-api-client';
import { DeletedApplicantComponent } from 'src/shared/error-pages/deleted-applicant/deleted-applicant.component';
import { PageNotFoundComponent } from 'src/shared/error-pages/page-not-found.component';
import { UnauthorizedComponent } from 'src/shared/error-pages/unauthorized.component';
import { hasFeatureToggle, permissedFactory } from 'src/shared/guards';
import { OrgService } from 'src/shared/org/org.service';
import { DirectoryService } from './directory/directory.service';
import { CommercialLinesAuthGuard } from './manage-carriers/carriers/commercial-lines-auth-guard';
import { ManageCarrierService } from './manage-carriers/manage-carriers.service';
import { ManageMyAgenciesAuthGuard } from './manage-carriers/manage-my-agencies-auth-guard';
import { CarrierService } from './manage-carriers/services/carrier.service';
import { RewriteGuard } from './policies/rewrites/rewrites.guard';
import { RewritesService } from './policies/rewrites/rewrites.service';

export const appRoutes: Routes = [
  {
    path: 'dashboard',
    loadChildren: () => import('./dashboard/dashboard.module')
      .then(mod => mod.DashboardModule),
    title: 'Dashboard',
  },
  {
    path: 'account',
    loadChildren: () => import('./account/account.module')
      .then(mod => mod.AccountModule),
  },
  {
    path: 'policies',
    children: [
      {
        path: 'rewrites',
        loadComponent: () => import('./policies/rewrites/rewrites.component'),
        providers: [RewriteGuard, RewritesService],
        canActivate: [RewriteGuard],
        title: 'Rewrites',
      },
    ],
  },
  {
    path: 'proposal-soi-overview',
    loadChildren: () => import('./settings/proposal-soi-template/proposal-soi-template.module')
      .then(mod => mod.ProposalSoiTemplateModule),
  },
  {
    path: 'accounting',
    loadChildren: () => import('./accounting/accounting.module')
      .then(mod => mod.AccountingModule),
  },
  {
    path: 'submission-center',
    loadChildren: () => import('./submission-center/submission-center.module')
      .then(mod => mod.SubmissionCenterModule),
  },
  {
    path: 'marketplace',
    loadChildren: () => import('./marketplace/marketplace.routes')
      .then(mod => mod.marketplaceRoutes),
  },
  {
    path: 'labels',
    loadChildren: () => import('src/app/labels/labels.routes'),
  },
  {
    path: 'certificates-settings',
    loadChildren: () => import('./certificate-settings/certificates-settings.module')
      .then(mod => mod.CertificateSettingsModule),
    canActivate: [
      (): MaybeAsync<GuardResult> => permissedFactory((user: User) => user.permissions.applicant.documents &&
        user.permissions.documentTemplate.certificateMaster),
    ],
  },
  {
    path: 'workflow',
    loadChildren: () => import('./workflow/workflow.module')
      .then(mod => mod.WorkflowModule),
  },
  {
    path: 'goals',
    loadChildren: () => import('./goals/goals.module')
      .then(mod => mod.GoalsModule),
    title: 'Agency Performance',
  },
  {
    path: 'looker-reports',
    loadChildren: () => import('./looker-reports/looker-reports.module')
      .then(mod => mod.LookerReportsModule),
    canActivate: [
      (): GuardResult => permissedFactory((user: User) => user.permissions.lookerReports.isViewer ||
        user.permissions.lookerReports.isExporter ||
        user.permissions.lookerReports.viewCarrierReports),
    ],
  },
  {
    path: 'rating/manage-carriers',
    loadChildren: () => import('./manage-carriers/manage-carriers.routing')
      .then(mod => mod.manageCarriersRoutes),
    providers: [
      CarrierService,
      ManageCarrierService,
      OrgService,
      CommercialLinesAuthGuard,
      ManageMyAgenciesAuthGuard,
      { provide: CarriersClient, useValue: new CarriersClient() },
      { provide: IdpCarrierManagementClient, useValue: new IdpCarrierManagementClient() },
    ],
  },
  {
    path: 'communication',
    loadChildren: () => import('./communication/communication.module')
      .then(mod => mod.CommunicationModule),
  },
  {
    path: 'user-settings',
    loadChildren: () => import('./user-settings/user-settings.module')
      .then(mod => mod.UserSettingsModule),
  },
  {
    path: 'uw-requests',
    loadComponent: () => import('./uw-requests/uw-requests-dashboard/uw-requests-dashboard.component'),
    title: 'Underwriting Requests',
  },
  {
    path: 'uw-request/:uwRequestId',
    loadChildren: () => import('./uw-requests/uw-request/uw-request.module')
      .then(mod => mod.UwRequestModule),
  },
  {
    path: 'policy-management',
    loadChildren: () => import('./policy-management/policy-management.module')
      .then(mod => mod.PolicyManagementModule),
  },
  {
    path: 'quoting-automation',
    loadChildren: () => import('./quoting-automation/quoting-automation.routing')
      .then(mod => mod.quotingAutomationRoutes),
  },
  {
    path: 'sales-center',
    loadChildren: () => import('./sales-center/sales-center.routes')
      .then(file => file.routes),
  },
  {
    path: 'activity',
    loadChildren: () => import('src/shared/activity/activity.module')
      .then(r => r.ActivityModule),
    title: 'Agency - Activity',
  },
  {
    path: 'tasks',
    loadChildren: () => import('src/shared/task/task.module')
      .then(r => r.TaskModule),
    title: 'Agency - Tasks',
  },
  {
    path: 'email',
    loadChildren: () => import('src/shared/email/email.module')
      .then(r => r.EmailModule),
  },
  {
    path: 'client-center-admin',
    loadChildren: () => import('./client-center-admin/client-center-admin.routes')
      .then(mod => mod.clientCenterAdminRoutes),
  },
  {
    path: 'eva/chat-history',
    loadComponent: () => import('./eva-sidebar/eva-chat-history/eva-chat-history.component')
      .then(m => m.EvaChatHistoryComponent),
    canActivate: [
      (): GuardResult => permissedFactory(
        (user: User) => !!user.permissions.ai.evaChatAssist && !!user.products.ezLynxAI
      ),
    ],
    title: 'EVA Chat History',
  },
  {
    path: 'eva',
    loadChildren: () => import('../shared/eva/eva.routes')
      .then(routes => routes.evaRoutes),
    canActivate: [
      (): GuardResult => permissedFactory(
        (user: User) => user.permissions.ai.emailContentAssist && user.products.ezLynxAI
      ),
    ],
  },
  {
    path: 'retention-center',
    loadChildren: () => import('./retention/retention-center-routing')
      .then(mod => mod.retentionCenterRoutes),
    title: 'Retention Center',
  },
  {
    path: 'directory',
    loadChildren: () => import('./directory/directory.routes')
      .then(file => file.routes),
    providers: [
      DirectoryService,
      { provide: DirectoryClient, useValue: new DirectoryClient() },
    ],
    canActivate: [
      (): Promise<GuardResult> => hasFeatureToggle(() => inject(DirectoryClient).getFeatureToggle()),
    ],
  },
  {
    path: 'esignature',
    children: [
      {
        path: '',
        loadChildren: () => import('./account/documents/signature-envelope/signature-envelope.routes')
          .then(mod => mod.signatureEnvelopeRoutes),
      },
    ],
  },
  {
    path: 'autofill',
    loadChildren: () => import('./autofill/autofill.routes').then(mod => mod.autofillRoutes),
    title: 'EVA AutoFill',
  },
  {
    path: 'commissions',
    loadChildren: () => import('./commissions/commissions.routes')
      .then(mod => mod.routes),
    title: 'Commissions',
  },
  { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
  { path: 'errors/delete-applicant', component: DeletedApplicantComponent, title: 'Applicant Deleted' },
  { path: 'errors/403', component: UnauthorizedComponent, title: 'Unauthorized' },
  { path: 'errors/404', component: PageNotFoundComponent, title: 'Page Not Found' }, // TODO: Might have to disable this, replace with real 404 component
  { path: '**', redirectTo: 'errors/404' },
];

/** Paths that hide the app shell (top banner + left nav). */
export const noShellRootPaths = [
  /account\/\d+\/documentforms/,
  /asset-import/,
  /eva\/ask-eva/,
];

/**
 * Paths that hide any nested shell rendered over a sub-route (e.g. a contextual
 * header, tab bar, or side panel) in addition to everything in `noShellRootPaths`.
 * Sub-routes listed here render their own chrome, so the surrounding shell would
 * stack awkwardly — the app-level shell is still kept visible by default.
 */
export const noNestedShellRootPaths = [
  ...noShellRootPaths,
  /account\/\d+\/(rating|policy)\/autofill/,
];
